<script setup lang="ts">
import AlertPopupWindow from "@/components/alert-popup/AlertPopupWindow.vue";
import GenericDialog from "@/components/dialog-wrappers/GenericDialog.vue";
import CustomSolidButton from "@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue";
import VehicleSearchContent from "@/components/vehicles/VehicleSearchContent.vue";
import Coordinate from "@/domains/coordinates/coordinate";
import { canEditLocation } from "@/domains/locations/locationPermissions.ts";
import { useSavedLocationStore } from "@/domains/locations/savedLocationStore.ts";
import { useUserStore } from "@/domains/user/userStore.ts";
import { useVehicleStore, type Vehicle } from "@/domains/vehicles";
import { useDashboardNavStore } from "@/stores/dashboardNavStore.ts";
import { useFeatureStore } from "@/stores/featureStore.ts";
import { useMapStore } from "@/stores/mapStore";
import { getNiceDuration } from "@/utils/timeUtils";
import { computed, ref, watch } from "vue";
import FavLocationForm from "./FavLocationForm.vue";

const dashboardNavStore = useDashboardNavStore();
const savedLocationsStore = useSavedLocationStore();
const featureStore = useFeatureStore();
const userStore = useUserStore();
const vehicleStore = useVehicleStore();
const mapStore = useMapStore();

const locationData = computed(() => {
  const targetId = dashboardNavStore.displayedLocationId;
  if (!targetId) return null;
  return savedLocationsStore.getLocationById(targetId) ?? null;
});

/** Fleet vehicles that have this location set as their home/depot location. */
const vehiclesBasedHere = computed(() => {
  if (!locationData.value?.externalId) return [];
  return vehicleStore.vehicles.filter(
    (vehicle) => vehicle.homeLocationExternalId === locationData.value?.externalId,
  );
});

// Reuses the same vehicle-picker/modal pairing as HomeSelectedVehicleCard.vue
// and TripSelectedVehicleCard.vue, rather than a bespoke picker here.
const showVehicleSearchDialog = ref(false);

/** Assigns the selected vehicle's home location to this location. */
async function handleVehicleSelected(vehicle: Vehicle): Promise<void> {
  showVehicleSearchDialog.value = false;

  if (!vehicle.externalId || !locationData.value?.externalId) return;

  await vehicleStore.updateVehicle(vehicle.externalId, {
    homeLocationExternalId: locationData.value.externalId,
  });
}

// Reconciles vehiclesBasedHere against the backend's authoritative answer
// whenever a location is displayed - catches another user having
// reassigned a vehicle since this fleet's vehicles were last fetched.
watch(
  () => locationData.value?.externalId,
  (externalId) => {
    if (externalId) vehicleStore.refreshVehiclesForLocation(externalId);
  },
  { immediate: true },
);

function handleViewVehicleDetails(vehicleId: string): void {
  dashboardNavStore.showVehicleDetails(vehicleId);
}

const can_edit_location = computed<boolean>(() => {
  if (!locationData.value) return false;

  return canEditLocation(locationData.value, {
    canManageFleetLocations: featureStore.can("location-management"),
    currentUserId: userStore.getCurrentUser?.id,
  });
});

const show_edit_form = ref(false);

const show_delete_confirmation = ref(false);
const async_delete_in_progress = ref(false);
const delete_outcome = ref<"ok" | "failed" | null>(null);
const handle_delete = async () => {
  if (!locationData.value) return;

  async_delete_in_progress.value = true;
  const res = await savedLocationsStore.deleteSavedLocation(
    locationData.value.localId,
  );
  if (res.success) {
    delete_outcome.value = "ok";
  } else {
    delete_outcome.value = "failed";
  }
  async_delete_in_progress.value = false;
};

const delete_message = computed(() => {
  if (async_delete_in_progress.value) return "Processing...";
  if (delete_outcome.value === "ok") return "Location successfully deleted.";
  if (delete_outcome.value === "failed")
    return "An error occurred while trying to delete this location.";
  return "This can not be undone, are you sure you want to delete this location.";
});

const delete_show_actions = computed(() => {
  if (async_delete_in_progress.value) return false;
  if (delete_outcome.value) return false;
  return true;
});

const clean_up_delete = () => {
  if (delete_outcome.value === "ok") {
    // navigate to list view
    dashboardNavStore.displayedContent = "fleet-locations";
  }
  // clean up local state
  show_delete_confirmation.value = false;
  async_delete_in_progress.value = false;
  delete_outcome.value = null;
};

const niceDuration = (seconds: number): string => getNiceDuration(seconds);

/**
 * Starts the map's fly-to animation for this location and closes the
 * dashboard so the map underneath is visible.
 */
function handleViewOnMap(): void {
  if (!locationData.value) return;

  mapStore.flyTo = new Coordinate({
    latitude: locationData.value.coordinates.latitude,
    longitude: locationData.value.coordinates.longitude,
  });
  dashboardNavStore.closeDashboard();
}
</script>

<style scoped>
.hover-black:hover {
  color: rgb(var(--v-theme-primary)) !important;
}
.hover-red:hover {
  color: rgb(var(--v-theme-error)) !important;
}
</style>

<template>
  <v-card v-if="locationData" flat class="pa-5 bg-pwt_snow">
    <!-- editable content -->
    <v-card v-if="show_edit_form" class="pa-5" color="transparent" flat>
      <FavLocationForm
        :initial-value="locationData"
        @close="() => (show_edit_form = false)"
      />
    </v-card>

    <!-- readonly content -->
    <template v-else>
      <!-- header card -->
      <v-card class="py-4 px-8 rounded-lg bg-pwt_snow mb-10" flat>
        <div class="d-flex flex-nowrap align-center">
          <div class="flex-grow-1 overflow-hidden">
            <div class="text-h6 text-truncate">{{ locationData.name }}</div>
            <div class="font-weight-medium text-truncate">
              {{ locationData.address }}
            </div>
          </div>
          <v-btn
            icon
            flat
            color="transparent"
            density="compact"
            class="ml-2 flex-shrink-0"
            @click.stop="handleViewOnMap"
          >
            <v-icon
              icon="mdi-map-marker-radius-outline"
              color="grey-darken-2"
              class="hover-black"
            />
          </v-btn>
          <template v-if="can_edit_location">
            <v-btn
              icon
              flat
              color="transparent"
              density="compact"
              class="flex-shrink-0"
              @click.stop="
                () => {
                  show_edit_form = true;
                }
              "
            >
              <v-icon
                icon="mdi-pencil"
                color="grey-darken-2"
                class="hover-black"
              />
            </v-btn>
            <v-btn
              icon
              flat
              color="transparent"
              density="compact"
              class="flex-shrink-0"
              @click.stop="
                () => {
                  show_delete_confirmation = true;
                }
              "
            >
              <v-icon
                icon="mdi-trash-can-outline"
                color="grey-darking-2"
                class="hover-red"
              />
            </v-btn>
          </template>
        </div>
      </v-card>

      <!-- Location settings section -->
      <v-card class="py-4 px-8 rounded-lg mb-10 bg-pwt_snow" elevation="1">
        <v-card-title> Location settings </v-card-title>
        <v-card-text>
          <div class="pa-2 bg-background d-flex rounded mb-3">
            <v-icon icon="mdi-eye-outline" class="mr-3" color="primary" />
            Visibility: {{ locationData.visibility }}
          </div>
          <div class="pa-2 bg-background d-flex rounded mb-3">
            <v-icon icon="mdi-tag-outline" class="mr-3" color="primary" />
            Location type: {{ locationData.type }}
          </div>
        </v-card-text>
      </v-card>

      <!-- Trip planning defaults section -->
      <v-card class="py-4 px-8 rounded-lg mb-10 bg-pwt_snow" elevation="1">
        <v-card-title> Trip planning defaults </v-card-title>
        <v-card-text v-if="!locationData.planningData">
          No trip planning defaults have been set for this location.
        </v-card-text>
        <v-card-text v-else>
          <div
            v-if="locationData.planningData.loadWeightChange"
            class="pa-2 bg-background d-flex rounded mb-3"
          >
            <v-icon icon="mdi-weight-kilogram" class="mr-3" color="primary" />
            Load weight change: {{ locationData.planningData.loadWeightChange }} kg
          </div>
          <div
            v-if="
              locationData.planningData.chargeHere &&
              locationData.planningData.rating &&
              locationData.planningData.currentType
            "
            class="pa-2 bg-background d-flex rounded mb-3"
          >
            <v-icon icon="mdi-ev-station" class="mr-3" color="primary" />
            Charge here with {{ locationData.planningData.rating }}kW
            {{ locationData.planningData.currentType }} charger
          </div>
          <div
            v-if="locationData.planningData.chargeHere"
            class="pa-2 bg-background d-flex rounded mb-3"
          >
            <v-icon icon="mdi-battery-charging" class="mr-3" color="primary" />
            Normal state of charge after charging here:
            {{ locationData.planningData.SOCAfterCharging ?? 0 }}%
          </div>
          <div
            v-if="locationData.planningData.energyUsed"
            class="pa-2 bg-background d-flex rounded mb-3"
          >
            <v-icon icon="mdi-lightning-bolt" class="mr-3" color="primary" />
            Uses {{ locationData.planningData.energyUsed }}kWh energy for idle
            processes at this location
          </div>
          <div
            v-if="locationData.planningData.stayDuration"
            class="pa-2 bg-background d-flex rounded mb-3"
          >
            <v-icon icon="mdi-clock-outline" class="mr-3" color="primary" />
            Normal expected stay duration:
            {{ niceDuration(locationData.planningData.stayDuration) }}
          </div>
        </v-card-text>
      </v-card>

      <!-- Vehicles based here section -->
      <v-card class="py-4 px-8 rounded-lg mb-10 bg-pwt_snow" elevation="1">
        <v-card-title class="d-flex align-center justify-space-between">
          Vehicles based here
          <CustomSolidButton
            v-if="can_edit_location"
            @click="showVehicleSearchDialog = true"
          >
            Add vehicle
          </CustomSolidButton>
        </v-card-title>
        <v-card-text>
          <v-list v-if="vehiclesBasedHere.length">
            <v-list-item
              v-for="vehicle in vehiclesBasedHere"
              :key="vehicle.id"
              :title="vehicle.name ?? vehicle.licensePlate ?? 'unnamed vehicle'"
              class="rounded-lg cursor-pointer"
              @click="handleViewVehicleDetails(vehicle.id)"
            />
          </v-list>
          <div v-else class="text-medium-emphasis">
            No vehicles are based here yet.
          </div>
        </v-card-text>
      </v-card>
    </template>
  </v-card>
  <v-card v-else flat class="pa-5 bg-pwt_snow">
    <v-card-title class="text-center"> Location not found </v-card-title>
    <v-card-text class="text-center">
      The location you selected could not be found.
    </v-card-text>
  </v-card>
  <AlertPopupWindow
    :activator="show_delete_confirmation"
    :title="'Delete ' + (locationData?.address ?? 'this location')"
    :message="delete_message"
    @close="() => (show_delete_confirmation = false)"
    :display-do-not-show-again-input="false"
    :show-loading-animation="async_delete_in_progress"
    :show-actions="delete_show_actions"
    @continue="handle_delete"
    @after-leave="clean_up_delete"
  />
  <GenericDialog
    :activator="showVehicleSearchDialog"
    @close="showVehicleSearchDialog = false"
  >
    <VehicleSearchContent @vehicle-select="handleVehicleSelected" />
  </GenericDialog>
</template>
